
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

interface NewsItem {
  id: number;
  date: string;
  category: string;
  title: string;
  content: string;
}

const newsData: NewsItem[] = [
  {
    id: 1,
    date: '2026.01.01',
    category: 'お知らせ',
    title: 'ホームページ開設のお知らせ',
    content: '自社ホームページを開設いたしました。より良いサービスを提供できるよう、社員一同努めてまいります。'
  }
];

function NewsCard({ news, index }: { news: NewsItem; index: number }) {
  const animation = useScrollAnimation({ threshold: 0.2 });
  const navigate = useNavigate();

  return (
    <div
      ref={animation.ref as React.RefObject<HTMLDivElement>}
      className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-all p-6 cursor-pointer scroll-animate ${animation.isVisible ? 'is-visible' : ''}`}
      style={{ animationDelay: `${index * 0.1}s` }}
      onClick={() => navigate(`/news/${news.id}`)}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <span className="text-sm text-gray-500 whitespace-nowrap">{news.date}</span>
        <span className="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap w-fit bg-teal-100 text-teal-700">
          {news.category}
        </span>
      </div>
      <h2 className="text-xl font-bold mb-3 text-gray-900">{news.title}</h2>
      <p className="text-gray-700 leading-relaxed">{news.content}</p>
    </div>
  );
}

export default function NewsPage() {
  const heroAnimation = useScrollAnimation({ threshold: 0.2 });

  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO meta
    document.title = 'お知らせ | 株式会社鈴電エンジニア';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアからのお知らせ・最新情報をご覧いただけます。');

    // Schema
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${siteUrl}/news`,
      url: `${siteUrl}/news`,
      name: 'お知らせ | 株式会社鈴電エンジニア',
      description: '株式会社鈴電エンジニアからのお知らせ・最新情報をご覧いただけます。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${siteUrl}/#website` },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-news';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.title = '株式会社鈴電エンジニア';
      if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアは、電気設備工事を中心に、空調・消防設備工事など、建物に関わる各種設備工事を行っています。');
      const el = document.getElementById('schema-news');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="relative py-20 sm:py-24 bg-gradient-to-br from-sky-50 to-white">
        <div 
          ref={heroAnimation.ref as React.RefObject<HTMLDivElement>}
          className={`max-w-7xl mx-auto px-4 sm:px-6 text-center scroll-animate ${heroAnimation.isVisible ? 'is-visible' : ''}`}
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4">お知らせ</h1>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {newsData.map((news, index) => (
              <NewsCard key={news.id} news={news} index={index} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
